import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DictionaryWord, VocabularyAddSource } from 'app/types';
import { StatusFilterValue, slivWord, statusOf } from './meta';

export type ViewMode = 'table' | 'cards';
export type GroupBy = 'letter' | 'recent' | 'none';

export interface VocabPanelSettings {
  /** 2 / 3 / 4 cards per row in cards mode (desktop only — phone/tablet override). */
  cardsPerRow: number;
  /** Show the example sentence under the definition on cards. */
  showExample: boolean;
  /** Tint each row by its status. */
  hlLearned: boolean;
  hlActive: boolean;
}

const VM_KEY = 'vw2-view-mode';
const GB_KEY = 'vw2-group-by';
const ST_KEY = 'vw2-settings';

const DEFAULT_SETTINGS: VocabPanelSettings = {
  cardsPerRow: 3,
  showExample: true,
  hlLearned: true,
  hlActive: true,
};

const readLS = <T,>(key: string, fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v != null ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeLS = (key: string, val: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* no-op */
  }
};

const matchesSearch = (w: DictionaryWord, search: string): boolean => {
  if (!search) return true;
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    (w.word ?? '').toLowerCase().includes(q) ||
    (w.translation ?? '').toLowerCase().includes(q) ||
    (w.definition ?? '').toLowerCase().includes(q)
  );
};

const matchesStatus = (
  w: DictionaryWord,
  filter: StatusFilterValue,
): boolean => {
  if (filter === 'all') return true;
  // Compare against the 4-state enum (status if BE returned it, otherwise
  // derived from the legacy booleans).
  return statusOf(w) === filter;
};

export interface EditDraft {
  translation: string;
  definition: string;
}

export interface GroupedWords {
  key: string;
  /** Heading text. `null` means "no header" (single all-bucket). */
  label: string | null;
  /** Source tag — populated only for "recent" grouping; drives icon choice. */
  source?: VocabularyAddSource;
  /** Optional secondary line shown under the title (date / count / etc.). */
  sublabel?: string;
  words: DictionaryWord[];
  ids: number[];
}

const fmtDayUA = (iso?: string): string | null => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const fmtDayTimeUA = (iso?: string): string | null => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const defaultSourceLabel = (
  source: VocabularyAddSource | undefined,
): string => {
  switch (source) {
    case 'DICTIONARY_BULK':
      return 'Зі словника';
    case 'IN_ACTION':
      return 'З "В дії"';
    case 'VOCABULARY_TEST':
      return 'Тест словникового запасу';
    case 'LEARNING_SESSION':
      return 'Сесія вправ';
    case 'IMPORT':
      return 'Імпорт';
    case 'MANUAL':
      return 'Додано вручну';
    case 'UNKNOWN':
    default:
      return 'Додано раніше';
  }
};

const groupList = (
  list: DictionaryWord[],
  groupBy: GroupBy,
): GroupedWords[] => {
  if (groupBy === 'none' || list.length === 0) {
    return [
      {
        key: 'all',
        label: null,
        words: list,
        ids: list.map((w) => w.id),
      },
    ];
  }

  if (groupBy === 'recent') {
    // Words sorted DESC by addedAt (id-fallback for legacy rows).
    const sorted = [...list].sort((a, b) => {
      const av = a.addedAt ?? '';
      const bv = b.addedAt ?? '';
      if (av && bv) return bv.localeCompare(av);
      return b.id - a.id;
    });

    // Group by addBatchId so words added in the same user action collapse
    // into one informative group. Words missing a batchId (truly legacy
    // pre-V10 rows that the migration didn't catch) bucket per day.
    const map = new Map<string, GroupedWords>();
    sorted.forEach((w) => {
      const day = fmtDayUA(w.addedAt);
      const dayTime = fmtDayTimeUA(w.addedAt);
      const key =
        w.addBatchId ?? `nobatch-${day ?? `id-${w.id}`}`;
      if (!map.has(key)) {
        const headline =
          w.addSourceLabel ?? defaultSourceLabel(w.addSource);
        const label = dayTime ? `${dayTime} · ${headline}` : headline;
        map.set(key, {
          key,
          label,
          source: w.addSource ?? 'UNKNOWN',
          words: [],
          ids: [],
        });
      }
      const g = map.get(key)!;
      g.words.push(w);
      g.ids.push(w.id);
    });
    return [...map.values()];
  }

  // letter
  const sorted = [...list].sort((a, b) =>
    (a.word ?? '').toLowerCase().localeCompare((b.word ?? '').toLowerCase()),
  );
  const map = new Map<string, GroupedWords>();
  sorted.forEach((w) => {
    const key = ((w.word ?? '#')[0] ?? '#').toUpperCase();
    if (!map.has(key)) {
      map.set(key, { key, label: key, words: [], ids: [] });
    }
    const g = map.get(key)!;
    g.words.push(w);
    g.ids.push(w.id);
  });
  return [...map.values()];
};

export interface UseVocabularyWordsViewState {
  // ── data
  filtered: DictionaryWord[];
  grouped: GroupedWords[];

  // ── filters
  search: string;
  setSearch: (v: string) => void;
  statusFilter: StatusFilterValue;
  setStatusFilter: (v: StatusFilterValue) => void;
  posFilter: string;
  setPosFilter: (v: string) => void;

  // ── grouping / view
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  viewMode: ViewMode;
  setViewMode: (m: ViewMode) => void;
  addOpen: boolean;
  setAddOpen: (v: boolean) => void;
  settingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;

  // ── selection
  selected: Record<number, boolean>;
  selectedCount: number;
  selectedIds: number[];
  toggle: (id: number) => void;
  toggleMany: (ids: number[]) => void;
  clear: () => void;
  allSelected: boolean;
  toggleAll: () => void;

  // ── editing
  isEditing: (id: number) => boolean;
  draftFor: (id: number) => EditDraft;
  startEdit: (ids: number[]) => void;
  startEditSelected: () => void;
  setDraft: (id: number, fields: Partial<EditDraft>) => void;
  saveEdit: (id: number) => void;
  cancelEdit: (id: number) => void;
  editingCount: number;
  saveAllEdits: () => void;
  cancelAllEdits: () => void;

  // ── panel settings
  settings: VocabPanelSettings;
  setSettings: <K extends keyof VocabPanelSettings>(
    k: K,
    v: VocabPanelSettings[K],
  ) => void;
  resetPanelSettings: () => void;

  // ── highlight (after add)
  highlightId: number | null;
  setHighlightId: (id: number | null) => void;

  // ── helpers
  sliv: (n: number) => string;
}

export interface UseVocabularyWordsViewParams {
  words: DictionaryWord[];
  /** Called whenever an inline edit (translation / definition) is saved. */
  onPatchWord?: (id: number, fields: Partial<EditDraft>) => void;
  /** Reset selection when this counter ticks (e.g. page changed). */
  resetKey?: number;
}

export const useVocabularyWordsView = ({
  words,
  onPatchWord,
  resetKey = 0,
}: UseVocabularyWordsViewParams): UseVocabularyWordsViewState => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [posFilter, setPosFilter] = useState<string>('all');

  const [groupBy, setGroupByState] = useState<GroupBy>(() =>
    readLS<GroupBy>(GB_KEY, 'letter'),
  );
  const setGroupBy = useCallback((g: GroupBy) => {
    setGroupByState(g);
    writeLS(GB_KEY, g);
  }, []);

  const [viewMode, setViewModeState] = useState<ViewMode>(() =>
    readLS<ViewMode>(VM_KEY, 'table'),
  );
  const setViewMode = useCallback((m: ViewMode) => {
    setViewModeState(m);
    writeLS(VM_KEY, m);
  }, []);

  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [settings, setSettingsState] = useState<VocabPanelSettings>(() =>
    readLS<VocabPanelSettings>(ST_KEY, DEFAULT_SETTINGS),
  );
  const setSettings = useCallback(
    <K extends keyof VocabPanelSettings>(k: K, v: VocabPanelSettings[K]) => {
      setSettingsState((prev) => {
        const next = { ...prev, [k]: v };
        writeLS(ST_KEY, next);
        return next;
      });
    },
    [],
  );
  const resetPanelSettings = useCallback(() => {
    setSettingsState(DEFAULT_SETTINGS);
    writeLS(ST_KEY, DEFAULT_SETTINGS);
  }, []);

  // ── selection
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  useEffect(() => {
    setSelected({});
    setEditingIds({});
    setDrafts({});
  }, [resetKey]);

  // ── editing
  const [editingIds, setEditingIds] = useState<Record<number, boolean>>({});
  const [drafts, setDrafts] = useState<Record<number, EditDraft>>({});

  const wordsRef = useRef(words);
  wordsRef.current = words;

  const isEditing = useCallback((id: number) => !!editingIds[id], [editingIds]);
  const draftFor = useCallback(
    (id: number): EditDraft => drafts[id] ?? { translation: '', definition: '' },
    [drafts],
  );

  const startEdit = useCallback((ids: number[]) => {
    setEditingIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const w = wordsRef.current.find((x) => x.id === id);
        if (w) {
          next[id] = {
            translation: w.translation ?? '',
            definition: w.definition ?? '',
          };
        }
      });
      return next;
    });
  }, []);

  const setDraft = useCallback(
    (id: number, fields: Partial<EditDraft>) => {
      setDrafts((prev) => ({
        ...prev,
        [id]: { ...(prev[id] ?? { translation: '', definition: '' }), ...fields },
      }));
    },
    [],
  );

  const endEdit = useCallback((id: number) => {
    setEditingIds((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const saveEdit = useCallback(
    (id: number) => {
      const d = drafts[id];
      if (d) onPatchWord?.(id, d);
      endEdit(id);
    },
    [drafts, endEdit, onPatchWord],
  );

  const cancelEdit = useCallback((id: number) => endEdit(id), [endEdit]);

  const cancelAllEdits = useCallback(() => {
    setEditingIds({});
    setDrafts({});
  }, []);

  const saveAllEdits = useCallback(() => {
    Object.keys(editingIds).forEach((k) => {
      const id = Number(k);
      const d = drafts[id];
      if (editingIds[id] && d) onPatchWord?.(id, d);
    });
    setEditingIds({});
    setDrafts({});
  }, [drafts, editingIds, onPatchWord]);

  const editingCount = Object.values(editingIds).filter(Boolean).length;

  // ── derived
  const filtered = useMemo(
    () =>
      words.filter(
        (w) =>
          matchesStatus(w, statusFilter) &&
          (posFilter === 'all' || (w.partOfSpeech ?? '') === posFilter) &&
          matchesSearch(w, search),
      ),
    [words, statusFilter, posFilter, search],
  );

  const grouped = useMemo(() => groupList(filtered, groupBy), [
    filtered,
    groupBy,
  ]);

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([id]) => Number(id)),
    [selected],
  );
  const selectedCount = selectedIds.length;
  const allSelected =
    filtered.length > 0 && filtered.every((w) => !!selected[w.id]);

  const toggle = useCallback(
    (id: number) => setSelected((p) => ({ ...p, [id]: !p[id] })),
    [],
  );
  const toggleMany = useCallback(
    (ids: number[]) =>
      setSelected((p) => {
        const allSel = ids.length > 0 && ids.every((id) => p[id]);
        const next = { ...p };
        ids.forEach((id) => {
          if (allSel) delete next[id];
          else next[id] = true;
        });
        return next;
      }),
    [],
  );
  const clear = useCallback(() => setSelected({}), []);
  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const all = filtered.every((w) => prev[w.id]);
      const next = { ...prev };
      if (all) {
        filtered.forEach((w) => {
          delete next[w.id];
        });
      } else {
        filtered.forEach((w) => {
          next[w.id] = true;
        });
      }
      return next;
    });
  }, [filtered]);

  const startEditSelected = useCallback(() => {
    if (selectedIds.length) startEdit(selectedIds);
  }, [selectedIds, startEdit]);

  // highlight
  const [highlightId, setHighlightId] = useState<number | null>(null);

  return {
    filtered,
    grouped,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    posFilter,
    setPosFilter,
    groupBy,
    setGroupBy,
    viewMode,
    setViewMode,
    addOpen,
    setAddOpen,
    settingsOpen,
    setSettingsOpen,
    selected,
    selectedCount,
    selectedIds,
    toggle,
    toggleMany,
    clear,
    allSelected,
    toggleAll,
    isEditing,
    draftFor,
    startEdit,
    startEditSelected,
    setDraft,
    saveEdit,
    cancelEdit,
    editingCount,
    saveAllEdits,
    cancelAllEdits,
    settings,
    setSettings,
    resetPanelSettings,
    highlightId,
    setHighlightId,
    sliv: slivWord,
  };
};
